/**
 * Servicio de API para Supabase - ControlCash
 * Todas las funciones usan auth.uid() vía RLS; userId se pasa para consistencia.
 */
import { supabase } from "../lib/supabase";

const mapMonthFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        year: row.year,
        monthNumber: row.month,
      }
    : null;

const mapIncomeFromDb = (row) =>
  row
    ? {
        id: row.id,
        monthId: row.month_id,
        name: row.name,
        amount: parseFloat(row.amount),
        currency: row.currency || "PEN",
      }
    : null;

const mapExpenseFromDb = (row) =>
  row
    ? {
        id: row.id,
        monthId: row.month_id,
        name: row.name,
        amount: parseFloat(row.amount),
        currency: row.currency || "PEN",
        category: row.category,
        status: row.status || "PENDING",
        dueDay: row.due_day,
        isFromRecurring: row.is_from_recurring ?? false,
        recurringTemplateId: row.recurring_template_id,
        remindDaysBefore: row.remind_days_before,
        date: row.date,
        paidAt: row.paid_at,
      }
    : null;

const mapTemplateFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        amount: parseFloat(row.amount),
        currency: row.currency || "PEN",
        category: row.category,
        dueDay: row.due_day,
        active: row.active ?? true,
        remindDaysBefore: row.remind_days_before,
      }
    : null;

const mapIncomeTemplateFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        amount: parseFloat(row.amount),
        currency: row.currency || "PEN",
        active: row.active ?? true,
      }
    : null;

const mapGoalFromDb = (row) =>
  row
    ? {
        id: row.id,
        name: row.name,
        targetAmount: parseFloat(row.target_amount),
        currency: row.currency || "PEN",
        targetDate: row.target_date,
        status: row.status || "ACTIVE",
      }
    : null;

const mapContributionFromDb = (row) =>
  row
    ? {
        id: row.id,
        goalId: row.goal_id,
        monthId: row.month_id,
        amount: parseFloat(row.amount),
        currency: row.currency || "PEN",
        date: row.date,
        note: row.note,
      }
    : null;

const mapCategoryFromDb = (row) =>
  row ? { id: row.id, name: row.name } : null;

// --- User settings ---
export async function fetchUserSettings(userId) {
  const { data, error } = await supabase
    .from("user_settings")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  
  // Log para debugging
  if (data) {
    console.log("📥 fetchUserSettings - Datos desde Supabase:", {
      enabled_currencies: data.enabled_currencies,
      tipo: typeof data.enabled_currencies,
      esArray: Array.isArray(data.enabled_currencies),
      cantidad: Array.isArray(data.enabled_currencies) ? data.enabled_currencies.length : 'N/A',
      updated_at: data.updated_at
    });
  }
  
  return data;
}

export async function upsertUserSettings(userId, { currency, usdToPenRate, exchangeRates, showAmounts, enabledCurrencies }) {
  const penRate = exchangeRates?.PEN ?? usdToPenRate ?? 3.75;
  const row = {
    user_id: userId,
    currency: currency ?? "PEN",
    usd_to_pen_rate: parseFloat(penRate ?? 3.75),
    show_amounts: showAmounts ?? true,
    updated_at: new Date().toISOString(),
  };
  
  // IMPORTANTE: Siempre incluir enabled_currencies cuando se pasa explícitamente
  // Si está definido, usarlo (incluso si es un array con más de 2 elementos)
  if (enabledCurrencies !== undefined) {
    if (Array.isArray(enabledCurrencies) && enabledCurrencies.length > 0) {
      // Guardar TODAS las monedas, no solo las primeras dos
      row.enabled_currencies = enabledCurrencies;
      console.log("💾 Guardando enabled_currencies completo:", enabledCurrencies);
    } else {
      // Solo usar default si el array está vacío o no es válido
      console.warn("⚠️ enabledCurrencies no es válido, usando default");
      row.enabled_currencies = ["PEN", "USD"];
    }
  }
  // Si enabledCurrencies es undefined, NO incluirlo en el row para que Supabase mantenga el valor existente
  
  if (exchangeRates !== undefined && typeof exchangeRates === "object") {
    row.exchange_rates = exchangeRates;
  }
  
  console.log("💾 Datos a guardar en user_settings:", {
    enabledCurrencies: row.enabled_currencies,
    hasEnabledCurrencies: row.enabled_currencies !== undefined,
    rowKeys: Object.keys(row)
  });
  
  // Log antes de guardar para verificar qué estamos enviando
  console.log("📤 Enviando a Supabase upsert:", {
    user_id: userId,
    enabled_currencies: row.enabled_currencies,
    cantidad: Array.isArray(row.enabled_currencies) ? row.enabled_currencies.length : 'N/A',
    todasLasKeys: Object.keys(row)
  });
  
  let { data, error } = await supabase
    .from("user_settings")
    .upsert(row, { onConflict: "user_id" })
    .select()
    .single();

  if (error) {
    // Si el error es porque la columna no existe, dar un mensaje más claro
    if (error.code === 'PGRST204' && error.message?.includes('enabled_currencies')) {
      const migrationError = new Error(
        "La columna 'enabled_currencies' no existe en la base de datos. " +
        "Por favor ejecuta la migración SQL en Supabase Dashboard → SQL Editor: " +
        "docs/supabase-migration-currency.sql"
      );
      migrationError.originalError = error;
      console.error("❌ ERROR CRÍTICO - Migración faltante:", migrationError.message);
      throw migrationError;
    }
    console.error("Error guardando user_settings:", error);
    throw error;
  }
  
  const savedEnabledCurrencies = data?.enabled_currencies;
  const savedArray = Array.isArray(savedEnabledCurrencies) 
    ? savedEnabledCurrencies 
    : (typeof savedEnabledCurrencies === 'string' ? JSON.parse(savedEnabledCurrencies) : null);
  
  console.log("✅ Datos guardados, respuesta de Supabase:", {
    enabled_currencies: savedArray,
    cantidad: savedArray?.length || 0,
    tipo: typeof savedEnabledCurrencies,
    esArray: Array.isArray(savedEnabledCurrencies),
    coincideConEnviado: savedArray && JSON.stringify([...savedArray].sort()) === JSON.stringify([...(row.enabled_currencies || [])].sort())
  });
  
  // Verificar que se guardaron todas las monedas
  if (savedArray && row.enabled_currencies && savedArray.length !== row.enabled_currencies.length) {
    console.error("❌ ERROR: No se guardaron todas las monedas!", {
      enviado: row.enabled_currencies,
      guardado: savedArray
    });
  }
  
  return data;
}

// --- Months ---
export async function fetchMonths(userId) {
  const { data, error } = await supabase
    .from("months")
    .select("*")
    .eq("user_id", userId)
    .order("year", { ascending: false })
    .order("month", { ascending: false });

  if (error) throw error;
  return (data || []).map(mapMonthFromDb);
}

export async function createMonth(userId, { name, monthNumber, year }) {
  const { data, error } = await supabase
    .from("months")
    .insert({
      user_id: userId,
      name,
      month: monthNumber,
      year,
    })
    .select()
    .single();

  if (error) throw error;
  return mapMonthFromDb(data);
}

export async function deleteMonth(userId, monthId) {
  const { error } = await supabase.from("months").delete().eq("id", monthId).eq("user_id", userId);
  if (error) throw error;
}

// --- Incomes ---
export async function fetchIncomes(userId) {
  const { data, error } = await supabase.from("incomes").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapIncomeFromDb);
}

export async function addIncome(userId, { monthId, name, amount, currency = "PEN" }) {
  const { data, error } = await supabase
    .from("incomes")
    .insert({
      user_id: userId,
      month_id: monthId,
      name,
      amount,
      currency: currency || "PEN",
    })
    .select()
    .single();

  if (error) throw error;
  return mapIncomeFromDb(data);
}

export async function updateIncome(userId, { incomeId, name, amount, currency }) {
  const updateData = { name, amount };
  if (currency !== undefined) updateData.currency = currency;
  
  const { data, error } = await supabase
    .from("incomes")
    .update(updateData)
    .eq("id", incomeId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapIncomeFromDb(data);
}

export async function deleteIncome(userId, incomeId) {
  const { error } = await supabase.from("incomes").delete().eq("id", incomeId).eq("user_id", userId);
  if (error) throw error;
}

// --- Expenses ---
export async function fetchExpenses(userId) {
  const { data, error } = await supabase.from("expenses").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapExpenseFromDb);
}

function expenseToRow(payload) {
  const row = {
    user_id: null,
    month_id: payload.monthId,
    name: payload.name,
    amount: payload.amount,
    currency: payload.currency || "PEN",
    category: payload.category ?? null,
    status: payload.status ?? "PENDING",
    due_day: payload.dueDay ?? null,
    is_from_recurring: payload.isFromRecurring ?? false,
    recurring_template_id: payload.recurringTemplateId ?? null,
    remind_days_before: payload.remindDaysBefore ?? null,
    date: payload.date ?? null,
    paid_at: payload.paidAt ?? null,
  };
  return row;
}

export async function addExpense(userId, payload) {
  const row = expenseToRow(payload);
  row.user_id = userId;

  const { data, error } = await supabase.from("expenses").insert(row).select().single();
  if (error) throw error;
  return mapExpenseFromDb(data);
}

export async function updateExpense(userId, expenseId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.amount !== undefined) row.amount = patch.amount;
  if (patch.currency !== undefined) row.currency = patch.currency;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.status !== undefined) row.status = patch.status;
  if (patch.dueDay !== undefined) row.due_day = patch.dueDay;
  if (patch.isFromRecurring !== undefined) row.is_from_recurring = patch.isFromRecurring;
  if (patch.recurringTemplateId !== undefined) row.recurring_template_id = patch.recurringTemplateId;
  if (patch.remindDaysBefore !== undefined) row.remind_days_before = patch.remindDaysBefore;
  if (patch.date !== undefined) row.date = patch.date;
  if (patch.paidAt !== undefined) row.paid_at = patch.paidAt;

  const { data, error } = await supabase
    .from("expenses")
    .update(row)
    .eq("id", expenseId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapExpenseFromDb(data);
}

export async function deleteExpense(userId, expenseId) {
  const { error } = await supabase.from("expenses").delete().eq("id", expenseId).eq("user_id", userId);
  if (error) throw error;
}

export async function toggleExpensePaid(userId, expenseId) {
  const { data: current } = await supabase.from("expenses").select("status, paid_at").eq("id", expenseId).eq("user_id", userId).single();
  if (!current) throw new Error("Gasto no encontrado");
  const newStatus = current.status === "PENDING" ? "PAID" : "PENDING";
  const paidAt = newStatus === "PAID" ? new Date().toISOString() : null;
  const { data, error } = await supabase
    .from("expenses")
    .update({ status: newStatus, paid_at: paidAt })
    .eq("id", expenseId)
    .eq("user_id", userId)
    .select()
    .single();
  if (error) throw error;
  return mapExpenseFromDb(data);
}

// --- Recurring templates ---
export async function fetchRecurringTemplates(userId) {
  const { data, error } = await supabase.from("recurring_templates").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapTemplateFromDb);
}

export async function createRecurringTemplate(userId, { name, amount, currency = "PEN", category, dueDay, active, remindDaysBefore }) {
  const { data, error } = await supabase
    .from("recurring_templates")
    .insert({
      user_id: userId,
      name,
      amount: amount ?? 0,
      currency: currency || "PEN",
      category: category ?? null,
      due_day: dueDay ?? null,
      active: active ?? true,
      remind_days_before: remindDaysBefore ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return mapTemplateFromDb(data);
}

export async function updateRecurringTemplate(userId, templateId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.amount !== undefined) row.amount = patch.amount;
  if (patch.currency !== undefined) row.currency = patch.currency;
  if (patch.category !== undefined) row.category = patch.category;
  if (patch.dueDay !== undefined) row.due_day = patch.dueDay;
  if (patch.active !== undefined) row.active = patch.active;
  if (patch.remindDaysBefore !== undefined) row.remind_days_before = patch.remindDaysBefore;

  const { data, error } = await supabase
    .from("recurring_templates")
    .update(row)
    .eq("id", templateId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapTemplateFromDb(data);
}

export async function deleteRecurringTemplate(userId, templateId) {
  const { error } = await supabase.from("recurring_templates").delete().eq("id", templateId).eq("user_id", userId);
  if (error) throw error;
}

// --- Recurring income templates ---
export async function fetchRecurringIncomeTemplates(userId) {
  const { data, error } = await supabase.from("recurring_income_templates").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapIncomeTemplateFromDb);
}

export async function createRecurringIncomeTemplate(userId, { name, amount, currency = "PEN", active }) {
  const { data, error } = await supabase
    .from("recurring_income_templates")
    .insert({
      user_id: userId,
      name,
      amount: amount ?? 0,
      currency: currency || "PEN",
      active: active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return mapIncomeTemplateFromDb(data);
}

export async function updateRecurringIncomeTemplate(userId, templateId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.amount !== undefined) row.amount = patch.amount;
  if (patch.currency !== undefined) row.currency = patch.currency;
  if (patch.active !== undefined) row.active = patch.active;

  const { data, error } = await supabase
    .from("recurring_income_templates")
    .update(row)
    .eq("id", templateId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapIncomeTemplateFromDb(data);
}

export async function deleteRecurringIncomeTemplate(userId, templateId) {
  const { error } = await supabase.from("recurring_income_templates").delete().eq("id", templateId).eq("user_id", userId);
  if (error) throw error;
}

// --- Goals ---
export async function fetchGoals(userId) {
  const { data, error } = await supabase.from("goals").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapGoalFromDb);
}

export async function createGoal(userId, { name, targetAmount, currency = "PEN", targetDate, status }) {
  const { data, error } = await supabase
    .from("goals")
    .insert({
      user_id: userId,
      name,
      target_amount: targetAmount ?? 0,
      currency: currency || "PEN",
      target_date: targetDate ?? null,
      status: status ?? "ACTIVE",
    })
    .select()
    .single();

  if (error) throw error;
  return mapGoalFromDb(data);
}

export async function updateGoal(userId, goalId, patch) {
  const row = {};
  if (patch.name !== undefined) row.name = patch.name;
  if (patch.targetAmount !== undefined) row.target_amount = patch.targetAmount;
  if (patch.currency !== undefined) row.currency = patch.currency;
  if (patch.targetDate !== undefined) row.target_date = patch.targetDate;
  if (patch.status !== undefined) row.status = patch.status;

  const { data, error } = await supabase
    .from("goals")
    .update(row)
    .eq("id", goalId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapGoalFromDb(data);
}

export async function deleteGoal(userId, goalId) {
  const { error } = await supabase.from("goals").delete().eq("id", goalId).eq("user_id", userId);
  if (error) throw error;
}

// --- Contributions ---
export async function fetchContributions(userId) {
  const { data, error } = await supabase.from("contributions").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapContributionFromDb);
}

export async function addContribution(userId, { goalId, monthId, amount, currency = "PEN", date, note }) {
  const { data, error } = await supabase
    .from("contributions")
    .insert({
      user_id: userId,
      goal_id: goalId,
      month_id: monthId ?? null,
      amount,
      currency: currency || "PEN",
      date: date ?? new Date().toISOString().split("T")[0],
      note: note ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return mapContributionFromDb(data);
}

export async function deleteContribution(userId, contributionId) {
  const { error } = await supabase.from("contributions").delete().eq("id", contributionId).eq("user_id", userId);
  if (error) throw error;
}

// --- Custom categories ---
export async function fetchCustomCategories(userId) {
  const { data, error } = await supabase.from("custom_categories").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapCategoryFromDb);
}

export async function createCustomCategory(userId, { id, name }) {
  const catId = id || `cat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const { data, error } = await supabase
    .from("custom_categories")
    .insert({
      id: catId,
      user_id: userId,
      name,
    })
    .select()
    .single();

  if (error) throw error;
  return mapCategoryFromDb(data);
}

export async function updateCustomCategory(userId, catId, { name }) {
  const { data, error } = await supabase
    .from("custom_categories")
    .update({ name })
    .eq("id", catId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapCategoryFromDb(data);
}

export async function deleteCustomCategory(userId, catId) {
  const { error } = await supabase.from("custom_categories").delete().eq("id", catId).eq("user_id", userId);
  if (error) throw error;
}

// --- Currency conversions ---
const mapConversionFromDb = (row) =>
  row
    ? {
        id: row.id,
        monthId: row.month_id,
        fromCurrency: row.from_currency,
        fromAmount: parseFloat(row.from_amount),
        toCurrency: row.to_currency,
        toAmount: parseFloat(row.to_amount),
        exchangeRate: parseFloat(row.exchange_rate),
        date: row.date,
        note: row.note,
      }
    : null;

export async function fetchCurrencyConversions(userId) {
  const { data, error } = await supabase.from("currency_conversions").select("*").eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapConversionFromDb);
}

export async function addCurrencyConversion(userId, { monthId, fromCurrency, fromAmount, toCurrency, toAmount, exchangeRate, date, note }) {
  const { data, error } = await supabase
    .from("currency_conversions")
    .insert({
      user_id: userId,
      month_id: monthId ?? null,
      from_currency: fromCurrency,
      from_amount: fromAmount,
      to_currency: toCurrency,
      to_amount: toAmount,
      exchange_rate: exchangeRate,
      date: date || new Date().toISOString().split("T")[0],
      note: note ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return mapConversionFromDb(data);
}

export async function updateCurrencyConversion(userId, conversionId, patch) {
  const row = {};
  if (patch.monthId !== undefined) row.month_id = patch.monthId;
  if (patch.fromCurrency !== undefined) row.from_currency = patch.fromCurrency;
  if (patch.fromAmount !== undefined) row.from_amount = patch.fromAmount;
  if (patch.toCurrency !== undefined) row.to_currency = patch.toCurrency;
  if (patch.toAmount !== undefined) row.to_amount = patch.toAmount;
  if (patch.exchangeRate !== undefined) row.exchange_rate = patch.exchangeRate;
  if (patch.date !== undefined) row.date = patch.date;
  if (patch.note !== undefined) row.note = patch.note;

  const { data, error } = await supabase
    .from("currency_conversions")
    .update(row)
    .eq("id", conversionId)
    .eq("user_id", userId)
    .select()
    .single();

  if (error) throw error;
  return mapConversionFromDb(data);
}

export async function deleteCurrencyConversion(userId, conversionId) {
  const { error } = await supabase.from("currency_conversions").delete().eq("id", conversionId).eq("user_id", userId);
  if (error) throw error;
}
